import React, { useEffect, useRef, useState } from "react";
import CodeEditor from "./CodeEditor";
import { PathMap, useCodeHelper } from "./CodeHelper";
import TextField from "@mui/material/TextField";
import IconButton from "components/Shared/IconButton";
import { Button } from "components/Shared";
import { evaluateXPath } from "requests/phoenixForms";
import { Control, XPathEvaluateResponse } from "interfaces/Phoenix.FormsDesigner.Models";
import { DialogProviderValue } from "components/Shared/DialogProvider";

interface Props {
  expression: string;
  onSave: (expression: string) => void;
  onCancel: () => void;
}

const ExpressionEditor = ({
  expression: expressionProps,
  onSave,
  onCancel,
}: Props) => {

  const codeHelper = useCodeHelper();
  const mounted = useRef<boolean>(true);
  const [expression, setExpression] = useState<string>(() => codeHelper.formatCode(expressionProps));
  const [pathMap, setPathMap] = useState<PathMap>({});
  const [calculateResult, setCalculateResult] = useState<string>();

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    setPathMap(codeHelper.updatePathMapFromExpression(expression));
  }, [expression]);

  const evaluateXPathSuccess = (response: XPathEvaluateResponse) => {
    setCalculateResult((response.error ? "Error evaluating: " : "") + response.value);
  }

  return (
    <div className="calculate-editor">
      <div className="calculate-header">
        <span>Result</span>
        <div className="calculate-result">{calculateResult}</div>
        <Button onClick={() => evaluateXPath(codeHelper.buildEvaluationPayload(expression), evaluateXPathSuccess, mounted)}>Calculate</Button>
      </div>
      <CodeEditor
        mode="xquery"
        value={expression}
        onChange={setExpression}
      />
      <div className="calculate-fields">
        {Object.entries(pathMap).map(([key, values]) => (
          <div className="calculate-field">
            <div className="field-name">{key}</div>
            <div className="text-fields">
              {values.map((v, i: number) => (
                <div className="single-field">
                  <TextField
                    variant="standard"
                    key={key + i}
                    onChange={(e) => setPathMap(codeHelper.updatePathMap(key, i, e.target.value))}
                    value={v}
                  />
                  {
                    i === 0 ?
                    <IconButton icon="add" onClick={() => setPathMap(codeHelper.updatePathMap(key, values.length, ""))} />
                    :
                    <IconButton icon="remove" onClick={() => setPathMap(codeHelper.removePathMapValue(key, i))} />
                  }
                </div>
              ))}
            </div>
          </div>))}
      </div>
      <div className="calculate-buttons">
        <Button onClick={() => onSave(expression)}>Save</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}


export const showExpressionEditor = (dialogContext: DialogProviderValue, title: string, initialExpression: string, onSave: (newValue: string) => void) => {
  dialogContext.show(
    title,
    <ExpressionEditor
      expression={initialExpression}
      onCancel={() => dialogContext.hide()}
      onSave={(newExpression: string) => {
        onSave(newExpression);
        dialogContext.hide();
      }}
    />, null, { className: "dialog-widthfix" });
};


export default ExpressionEditor;
